import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "./api";
import { useAuth } from "./auth";

const FavCtx = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const inflight = useRef(new Set());

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); setItems([]); return; }
    try {
      const { data } = await apiClient.get("/favorites");
      setItems(data.items || []);
      setIds(new Set(data.ids || []));
    } catch {
      setIds(new Set()); setItems([]);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFavorite = useCallback((id) => ids.has(id), [ids]);

  const toggle = useCallback(async (vehicleId) => {
    if (!user || inflight.current.has(vehicleId)) return;
    inflight.current.add(vehicleId);
    // Optimistic
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(vehicleId)) next.delete(vehicleId); else next.add(vehicleId);
      return next;
    });
    setBusy(true);
    try {
      await apiClient.post(`/favorites/${vehicleId}`);
      await refresh();
    } catch {
      await refresh();
    } finally {
      inflight.current.delete(vehicleId);
      setBusy(false);
    }
  }, [user, refresh]);

  const value = useMemo(() => ({ ids, items, isFavorite, toggle, refresh, busy, count: ids.size }), [ids, items, isFavorite, toggle, refresh, busy]);
  return <FavCtx.Provider value={value}>{children}</FavCtx.Provider>;
}

export function useFavorites() {
  const ctx = useContext(FavCtx);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
